// First Created - 27 Apr 2026
// Author - Atul Pandey

import {
    createSelector,
    createEntityAdapter,
} from '@reduxjs/toolkit'
import { apiSlice } from '@/app/api/apiSlice'

const accountContactMapsAdapter = createEntityAdapter({})

const initialState = accountContactMapsAdapter.getInitialState()

export const accountContactMapsApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getAccountContactMaps: builder.query({
            query: ({ accountId }) => ({
                url: '/accountContactMaps/getAccountContactMaps',
                params: { accountId },
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                }
            }),
            transformResponse: responseData => {
                const loadedAccountContactMaps = responseData.map(accountContactMap => {
                    accountContactMap.id = accountContactMap._id
                    return accountContactMap
                })
                return accountContactMapsAdapter.setAll(initialState, loadedAccountContactMaps)
            },
            providesTags: (result, error, arg) => {
                if (result?.ids) {
                    return [
                        { type: 'AccountContactMap', id: 'LIST' },
                        ...result.ids.map(id => ({ type: 'AccountContactMap', id }))
                    ]
                } else {
                    return [{ type: 'AccountContactMap', id: 'LIST' }]
                }
            },
        }),

        createNewAccountContactMap: builder.mutation({
            query: initialAccountContactMap => ({
                url: '/accountContactMaps/createNewAccountContactMap',
                method: 'POST',
                body: { ...initialAccountContactMap }
            }),
            invalidatesTags: [
                { type: 'AccountContactMap', id: 'LIST' }
            ]
        }),

        updateAccountContactMap: builder.mutation({
            query: initialAccountContactMap => ({
                url: '/accountContactMaps/updateAccountContactMap',
                method: 'PATCH',
                body: { ...initialAccountContactMap }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'AccountContactMap', id: arg.id },
                { type: 'AccountContactMap', id: 'LIST' }
            ],
        }),

        deleteAccountContactMap: builder.mutation({
            query: ({ id, deleteMode = "soft" }) => ({
                url: '/accountContactMaps/deleteAccountContactMap',
                method: 'DELETE',
                body: { id, deleteMode }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'AccountContactMap', id: arg.id },
                { type: 'AccountContactMap', id: 'LIST' }
            ]
        }),
    }),
})

export const {
    useGetAccountContactMapsQuery,
    useCreateNewAccountContactMapMutation,
    useUpdateAccountContactMapMutation,
    useDeleteAccountContactMapMutation,
} = accountContactMapsApiSlice

export const selectAccountContactMapResult =
    accountContactMapsApiSlice.endpoints.getAccountContactMaps.select()

const selectAccountContactMapsData = createSelector(
    selectAccountContactMapResult,
    accountContactMapResult => accountContactMapResult.data
)

export const {
    selectAll: selectAllAccountContactMaps,
    selectById: selectAccountContactMapById,
    selectIds: selectAccountContactMapIds,
} = accountContactMapsAdapter.getSelectors(
    state => selectAccountContactMapsData(state) ?? initialState
)